import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, DollarSign, ShoppingBag, Users,
  Percent, BarChart3, Loader2, ArrowUp, ArrowDown, PieChart as PieChartIcon,
  Calendar, Target, ChefHat, Lock
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend
} from "recharts";
import { API_URL } from "@/utils/apiConfig";

const API = API_URL;

const COLORS = ["#059669", "#f59e0b", "#3b82f6", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16"];

const SOURCE_LABELS = {
  direct: "Direto", website: "Website", ubereats: "Uber Eats",
  glovo: "Glovo", bolt_food: "Bolt Food", deliveroo: "Deliveroo",
  just_eat: "Just Eat", wolt: "Wolt",
};

function KpiCard({ icon: Icon, label, value, prev, suffix = "", prefix = "", growth, inverse = false }) {
  const isPositive = inverse ? growth < 0 : growth > 0;
  return (
    <div className="bg-white rounded-2xl border p-5 hover:shadow-md transition-shadow" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
          <Icon className="w-5 h-5 text-emerald-600" />
        </div>
        {growth !== undefined && growth !== 0 && (
          <Badge className={`text-[10px] ${isPositive ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"} border-0 gap-0.5`}>
            {isPositive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
            {Math.abs(growth)}%
          </Badge>
        )}
      </div>
      <p className="text-2xl font-black text-gray-900">{prefix}{typeof value === "number" ? value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : value}{suffix}</p>
      <p className="text-xs text-gray-400 mt-1">{label}</p>
    </div>
  );
}

export default function InvestorPage() {
  const { restaurantId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API}/api/v1/investor/dashboard/${restaurantId}?token=${token}`)
      .then(r => {
        if (!r.ok) throw new Error("Acesso negado");
        return r.json();
      })
      .then(body => setData(body?.data ?? body))
      .catch(e => setError(e.message || "Erro"))
      .finally(() => setLoading(false));
  }, [restaurantId, token]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <Lock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-lg font-bold text-gray-600">{error}</p>
        <p className="text-sm text-gray-400 mt-1">Verifique o token de acesso</p>
      </div>
    </div>
  );

  const k = data.kpis;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="investor-dashboard">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-emerald-600 font-bold uppercase tracking-wider mb-1">Modo Investidor</p>
              <h1 className="text-2xl font-black text-gray-900" data-testid="investor-restaurant-name">{data.restaurant_name}</h1>
              <p className="text-sm text-gray-400 flex items-center gap-1.5 mt-0.5">
                <Calendar className="w-3.5 h-3.5" /> {data.period}
              </p>
            </div>
            <div className="text-right">
              <img src="https://customer-assets.emergentagent.com/job_08f655cb-8b91-4c06-80bd-b94d5f4523ca/artifacts/kxmzqj5h_ChatGPT%20Image%2027%20de%20fev.%20de%202026%2C%2001_06_03.png" alt="Logo" className="w-10 h-10 rounded-xl" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard icon={DollarSign} label="Faturamento" value={k.revenue} prefix="" growth={k.revenue_growth} />
          <KpiCard icon={Target} label="Lucro Bruto" value={k.profit} growth={k.profit_growth} />
          <KpiCard icon={Percent} label="Margem Bruta" value={k.gross_margin} suffix="%" />
          <KpiCard icon={ShoppingBag} label="Ticket Medio" value={k.ticket_medio} prefix="" />
          <KpiCard icon={BarChart3} label="Pedidos" value={k.orders_count} />
          <KpiCard icon={ChefHat} label="CMV" value={k.cmv} suffix={` (${k.cmv_pct}%)`} inverse />
          <KpiCard icon={Users} label="Clientes Unicos" value={k.unique_customers} />
          <KpiCard icon={TrendingUp} label="Retencao" value={k.retention_rate} suffix="%" />
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Daily Revenue */}
          <div className="bg-white rounded-2xl border p-5" data-testid="chart-daily-revenue">
            <h3 className="text-sm font-bold text-gray-700 mb-4">Faturamento Diario (30 dias)</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.chart_daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={v => v.slice(8)} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => [`${Number(v).toFixed(2)} EUR`, "Faturamento"]} labelFormatter={l => l} />
                <Bar dataKey="revenue" fill="#059669" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Monthly Trend */}
          <div className="bg-white rounded-2xl border p-5" data-testid="chart-monthly-trend">
            <h3 className="text-sm font-bold text-gray-700 mb-4">Tendencia Mensal (6 meses)</h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.chart_monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => [`${Number(v).toFixed(2)} EUR`]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="revenue" name="Faturamento" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="expenses" name="Despesas" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="profit" name="Lucro" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Breakdown Row */}
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Revenue by Source */}
          <div className="bg-white rounded-2xl border p-5" data-testid="chart-revenue-source">
            <h3 className="text-sm font-bold text-gray-700 mb-4">Faturamento por Canal</h3>
            {data.revenue_by_source.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={data.revenue_by_source} dataKey="value" nameKey="source" cx="50%" cy="50%" outerRadius={80} label={({ source, percent }) => `${SOURCE_LABELS[source] || source} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    {data.revenue_by_source.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => [`${Number(v).toFixed(2)} EUR`]} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-gray-400 text-center py-8">Sem dados</p>
            )}
          </div>

          {/* Expense Breakdown */}
          <div className="bg-white rounded-2xl border p-5" data-testid="chart-expense-breakdown">
            <h3 className="text-sm font-bold text-gray-700 mb-4">Despesas por Categoria</h3>
            {data.expense_breakdown.length > 0 ? (
              <div className="space-y-2.5">
                {data.expense_breakdown.map((item, i) => {
                  const maxVal = Math.max(...data.expense_breakdown.map(e => e.value));
                  const pct = maxVal > 0 ? (item.value / maxVal * 100) : 0;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 w-28 truncate">{item.category}</span>
                      <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                      </div>
                      <span className="text-xs font-bold text-gray-700 w-20 text-right">{item.value.toFixed(2)} EUR</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-8">Sem despesas registradas</p>
            )}
          </div>
        </div>

        {/* Monthly Summary Table */}
        <div className="bg-white rounded-2xl border p-5" data-testid="monthly-summary-table">
          <h3 className="text-sm font-bold text-gray-700 mb-4">Resumo Mensal</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 text-gray-500 font-medium">Mes</th>
                  <th className="text-right py-2 text-gray-500 font-medium">Faturamento</th>
                  <th className="text-right py-2 text-gray-500 font-medium">Despesas</th>
                  <th className="text-right py-2 text-gray-500 font-medium">Lucro</th>
                  <th className="text-right py-2 text-gray-500 font-medium">Pedidos</th>
                </tr>
              </thead>
              <tbody>
                {data.chart_monthly.map((m, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2 font-medium text-gray-700">{m.month}</td>
                    <td className="py-2 text-right text-gray-600">{m.revenue.toFixed(2)} EUR</td>
                    <td className="py-2 text-right text-red-500">{m.expenses.toFixed(2)} EUR</td>
                    <td className={`py-2 text-right font-bold ${m.profit >= 0 ? "text-emerald-600" : "text-red-500"}`}>{m.profit.toFixed(2)} EUR</td>
                    <td className="py-2 text-right text-gray-600">{m.orders}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center py-4">
          <p className="text-[10px] text-gray-300">Dados gerados por Kaiso System - Modo Investidor</p>
        </div>
      </div>
    </div>
  );
}
